import React, { useState } from 'react';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import api from '../../utils/api';

const AdminLogin = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/admin-login', formData);
      
      if (response.data.success) {
        onLogin(response.data.data.token, response.data.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 text-9xl font-black text-white rotate-12">ADMIN</div>
        <div className="absolute bottom-20 right-20 text-9xl font-black text-white -rotate-12">ACCESS</div>
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white border-4 border-yellow-400 shadow-[16px_16px_0px_rgba(255,255,0,1)] p-8 md:p-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="bg-black border-4 border-yellow-400 p-4 mb-4">
              <img src="/owlfit-logo.png" alt="OWLFIT" className="w-full max-w-[260px] mx-auto object-contain" draggable="false" />
            </div>
            <div className="inline-block bg-black text-yellow-400 px-4 py-1 font-mono font-bold text-xs border-2 border-yellow-400">
              ADMIN_LOGIN_PORTAL
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border-4 border-red-600 flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-1" size={20} />
              <div className="font-bold text-sm text-red-900">{error}</div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Mail size={20} />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 border-4 border-black font-mono text-sm focus:outline-none focus:ring-4 focus:ring-yellow-400"
                  placeholder="admin@owlfit.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-black uppercase tracking-wider mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                  <Lock size={20} />
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-12 pr-4 py-3 border-4 border-black font-mono text-sm focus:outline-none focus:ring-4 focus:ring-yellow-400"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black text-lg uppercase py-4 border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'AUTHENTICATING...' : 'ACCESS SYSTEM'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-xs font-mono text-gray-500">
            AUTHORIZED_PERSONNEL_ONLY
          </div>
        </div>

        {/* Decorative Element */}
        <div className="mt-4 text-center">
          <div className="inline-block bg-yellow-400 text-black px-6 py-2 font-black text-xs border-4 border-black transform rotate-2">
            SECURE_CONNECTION_ESTABLISHED
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
